// Header.js
import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from '../../components/Logo';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const aboutTimeoutRef = useRef(null);
  const servicesTimeoutRef = useRef(null);

  const location = useLocation();
  // Transparent overlay header only on the homepage hero.
  const overlay = location.pathname === '/';

  const aboutLinks = [
    { name: 'Frequently Asked Questions', slug: 'faq' }
    // Reviews & Testimonials temporarily hidden:
    // { name: 'Reviews & Testimonials', slug: 'reviews-testimonials' }
  ];

  const homeCareServices = [
    { name: "Alzheimer's and Dementia Care", slug: 'alzheimers-dementia-care' },
    { name: 'Companionship Care', slug: 'companionship-care' },
    { name: 'Hospice Care', slug: 'hospice-care' },
    { name: 'Personal Care', slug: 'personal-care' },
    { name: 'Post-Operative Care', slug: 'post-operative-care' },
    { name: 'Respite Care', slug: 'respite-care' },
    { name: 'Specialty Care', slug: 'specialty-care' },
    { name: 'Spinal Cord Injury Care', slug: 'spinal-cord-injury-care' }
  ];

  const handleMouseEnter = (setter, timeoutRef) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setter(true);
  };

  const handleMouseLeave = (setter, timeoutRef) => {
    timeoutRef.current = setTimeout(() => setter(false), 150);
  };

  const wrapperClass = overlay
    ? 'absolute top-0 left-0 w-full z-50 bg-transparent'
    : 'sticky top-0 w-full z-50 bg-white border-b border-line';

  const linkColor = overlay
    ? 'text-white/90 hover:text-white'
    : 'text-ink/80 hover:text-primary';

  return (
    <header className={`${wrapperClass} px-6 md:px-12 lg:px-16 py-5`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* White logo on the transparent home hero, colour on solid pages. */}
        <Link to="/" className="flex items-center" aria-label="Wei Care home">
          <Logo variant={overlay ? 'white' : 'color'} className="h-9 md:h-11" />
        </Link>

        {/* Desktop Navigation */}
        <nav className={`hidden lg:flex items-center gap-9 text-sm font-medium ${linkColor}`}>
          <Link to="/" className="transition-colors">Home</Link>

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter(setIsServicesOpen, servicesTimeoutRef)}
            onMouseLeave={() => handleMouseLeave(setIsServicesOpen, servicesTimeoutRef)}
          >
            <button className="flex items-center gap-1 transition-colors">
              Services
              <ChevronDown size={15} />
            </button>
            {isServicesOpen && (
              <div className="absolute top-full left-0 pt-3 z-50">
                <div className="w-60 bg-white border border-line rounded-xl shadow-lg py-2">
                  {homeCareServices.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="block px-4 py-2.5 text-sm text-ink/80 hover:bg-cream hover:text-primary transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* About Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter(setIsAboutOpen, aboutTimeoutRef)}
            onMouseLeave={() => handleMouseLeave(setIsAboutOpen, aboutTimeoutRef)}
          >
            <button className="flex items-center gap-1 transition-colors">
              About
              <ChevronDown size={15} />
            </button>
            {isAboutOpen && (
              <div className="absolute top-full left-0 pt-3 z-50">
                <div className="w-60 bg-white border border-line rounded-xl shadow-lg py-2">
                  <Link to="/about" className="block px-4 py-2.5 text-sm text-ink/80 hover:bg-cream hover:text-primary transition-colors">About Us</Link>
                  {aboutLinks.map((link) => (
                    <Link
                      key={link.slug}
                      to={`/about/${link.slug}`}
                      className="block px-4 py-2.5 text-sm text-ink/80 hover:bg-cream hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Careers temporarily hidden from public nav */}
          {/* <Link to="/careers" className="transition-colors">Careers</Link> */}
          <Link to="/contact" className="transition-colors">Contact</Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden ${overlay ? 'text-white' : 'text-ink'}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg py-4 px-6 space-y-3 border-t border-line">
          <Link to="/" className="block py-2 font-medium text-ink hover:text-primary" onClick={() => setIsOpen(false)}>Home</Link>

          <div>
            <button
              className="w-full text-left font-medium text-ink py-2 flex items-center justify-between"
              onClick={() => setIsServicesOpen(!isServicesOpen)}
            >
              Services
              <ChevronDown size={16} className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {isServicesOpen && (
              <div className="pl-4 space-y-2 mt-1">
                {homeCareServices.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="block py-2 text-sm text-muted hover:text-primary"
                    onClick={() => setIsOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <button
              className="w-full text-left font-medium text-ink py-2 flex items-center justify-between"
              onClick={() => setIsAboutOpen(!isAboutOpen)}
            >
              About
              <ChevronDown size={16} className={`transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
            </button>
            {isAboutOpen && (
              <div className="pl-4 space-y-2 mt-1">
                <Link to="/about" className="block py-2 text-sm text-muted hover:text-primary" onClick={() => setIsOpen(false)}>About Us</Link>
                {aboutLinks.map((link) => (
                  <Link
                    key={link.slug}
                    to={`/about/${link.slug}`}
                    className="block py-2 text-sm text-muted hover:text-primary"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Careers temporarily hidden from public nav */}
          {/* <Link to="/careers" className="block py-2 font-medium text-ink hover:text-primary" onClick={() => setIsOpen(false)}>Careers</Link> */}
          <Link
            to="/contact"
            className="block w-full text-center bg-primary hover:bg-primary-dark text-white font-semibold px-6 py-2.5 rounded-full transition-colors mt-2"
            onClick={() => setIsOpen(false)}
          >
            Contact
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
